import React, { useState, useEffect, useRef } from 'react';
import { database, createRealtimeListener } from '../firebase';
import { ref, set } from 'firebase/database';
import { ArrowLeft, Plus, Sparkles, RefreshCw, Check, Calendar, FileText, Github } from 'lucide-react';
import { buildGithubPromptBlock } from '../utils/github';
import { aiChat } from '../lib/aiApi';
import './TodoDetailPage.css';

const TodoDetailPage = ({ userId, project, todoIndex, todo, onBack }) => {
  const [projectData, setProjectData] = useState(project);
  const [newSubText, setNewSubText] = useState('');
  const [newSubDate, setNewSubDate] = useState('');
  const [suggestions, setSuggestions] = useState(todo?.aiSuggestions || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const generatedOnce = useRef(false);

  // Live project data so todo updates reflect immediately
  useEffect(() => {
    if (!userId || !project?.id) return;
    const off = createRealtimeListener(`users/${userId}/projects/${project.id}`, (snap) => {
      const data = snap.val();
      if (!data) return;
      setProjectData({
        ...data,
        id: project.id,
        todos: Array.isArray(data.todos) ? data.todos : [],
      });
    });
    return () => off && off();
  }, [userId, project?.id]);

  const currentTodo = projectData?.todos?.[todoIndex] || todo;
  const subTodos = Array.isArray(currentTodo?.subTodos) ? currentTodo.subTodos : [];

  const writeTodo = async (updates) => {
    if (!userId) return;
    const todos = [...(projectData?.todos || [])];
    todos[todoIndex] = { ...todos[todoIndex], ...updates };
    await set(ref(database, `users/${userId}/projects/${project.id}/todos`), todos);
  };

  const addSubTodo = async () => {
    const text = newSubText.trim();
    const dueDate = newSubDate.trim();
    if (!text || !dueDate) return;
    const updated = [
      ...subTodos,
      { task: text, completed: false, dueDate, createdAt: Date.now() },
    ];
    await writeTodo({ subTodos: updated });
    setNewSubText('');
    setNewSubDate('');
  };

  const toggleSubTodo = async (idx) => {
    const updated = [...subTodos];
    const wasDone = !!updated[idx].completed;
    updated[idx] = {
      ...updated[idx],
      completed: !wasDone,
      completedAt: !wasDone ? Date.now() : null,
    };
    await writeTodo({ subTodos: updated });
  };

  const setDueDate = async (dateString) => {
    // dateString like "2026-05-12" or "" to clear
    await writeTodo({ dueDate: dateString ? dateString : null });
  };

  const toggleMain = async () => {
    const wasDone = !!currentTodo.completed;
    await writeTodo({
      completed: !wasDone,
      completedAt: !wasDone ? Date.now() : null,
    });
  };

  const generateSuggestions = async () => {
    setLoading(true);
    setError(null);

    const industry = projectData?.industry || project?.industry || '';
    const bookContext = projectData?.bookContext || project?.bookContext || '';
    const projectName = projectData?.name || project?.name || '';
    const taskTitle = currentTodo?.task || '';

    const aiDocs = projectData?.aiDocs && typeof projectData.aiDocs === 'object' ? Object.values(projectData.aiDocs) : [];
    const PDF_BUDGET = 60_000;
    let used = 0;
    const pdfBlocks = [];
    for (const doc of aiDocs) {
      if (!doc?.text) continue;
      const remaining = PDF_BUDGET - used;
      if (remaining <= 500) break;
      const snippet = doc.text.length > remaining ? doc.text.slice(0, remaining) + '\n[...truncated]' : doc.text;
      pdfBlocks.push(`--- ${doc.name} (${doc.pages || '?'} pages) ---\n${snippet}`);
      used += snippet.length;
    }
    const pdfBlock = pdfBlocks.length ? `\n\nReference PDFs:\n${pdfBlocks.join('\n\n')}` : '';

    const githubBlock = projectData?.github ? `\n\n${buildGithubPromptBlock(projectData.github)}` : '';

    const systemPrompt = `You are an expert advisor. Given a task in a project, produce concrete, actionable suggestions grounded in the supplied industry context, book/framework references, attached PDFs, and connected codebase.

Return STRICT JSON in this shape:
{
  "subTasks": ["..."],
  "insights": ["..."],
  "bookReferences": [{"title": "...", "takeaway": "..."}]
}

Rules:
- 4-6 subTasks: small, executable steps to complete the task
- 3-4 insights: industry-specific tactics, pitfalls, or leverage points
- 2-3 bookReferences: relate the task to ideas from the supplied book context (or well-known books in the industry if none provided). Each takeaway must be specific to this task.
- Be direct. No filler.`;

    const userPrompt = `Project: ${projectName}
Industry: ${industry || 'not specified'}
Book context: ${bookContext || 'not specified'}
Task: ${taskTitle}${pdfBlock}${githubBlock}

Generate suggestions now. When grounded in the supplied PDFs or codebase, reference them concretely (e.g. file paths, page contents) in insights and bookReferences.`;

    try {
      const data = await aiChat({
        task: 'classify',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        temperature: 0.6,
        max_tokens: 1200,
      });

      const content = data.choices?.[0]?.message?.content?.trim() || '';
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        setError('AI returned unexpected format.');
        return;
      }
      const parsed = JSON.parse(jsonMatch[0]);
      setSuggestions(parsed);
      await writeTodo({ aiSuggestions: parsed, aiSuggestionsAt: Date.now() });
    } catch (e) {
      console.error('AI suggestion error:', e);
      setError('Connection error. Try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate once on mount if not cached
  useEffect(() => {
    if (generatedOnce.current) return;
    generatedOnce.current = true;
    if (!suggestions) {
      generateSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const importSubTask = async (text) => {
    if (!text) return;
    const updated = [...subTodos, { task: text, completed: false, createdAt: Date.now() }];
    await writeTodo({ subTodos: updated });
  };

  const completedCount = subTodos.filter((s) => s.completed).length;
  const totalCount = subTodos.length;

  return (
    <div className="todo-detail-page">
      <div className="todo-detail-header">
        <button className="detail-back-btn" onClick={onBack}>
          <ArrowLeft size={18} />
          <span>Board</span>
        </button>
        <div className="detail-breadcrumb">
          {project?.image && <img src={project.image} alt="" className="detail-crumb-logo" />}
          <span>{projectData?.name || project?.name}</span>
        </div>
      </div>

      <div className="todo-detail-layout">
        <div className="todo-detail-main">
          <div className="main-todo-card">
            <button
              className={`main-todo-check ${currentTodo?.completed ? 'done' : ''}`}
              onClick={toggleMain}
              aria-label="Toggle main todo"
            >
              {currentTodo?.completed && <Check size={16} />}
            </button>
            <div className="main-todo-content">
              <div className="main-todo-eyebrow">Main task</div>
              <h2 className={`main-todo-title ${currentTodo?.completed ? 'done' : ''}`}>
                {currentTodo?.task}
              </h2>
              {totalCount > 0 && (
                <div className="main-todo-progress">
                  {completedCount}/{totalCount} sub-tasks complete
                </div>
              )}
              <div className="main-todo-due">
                <Calendar size={14} />
                <span className="main-todo-due-label">Due:</span>
                <input
                  type="date"
                  className="main-todo-due-input"
                  value={currentTodo?.dueDate || ''}
                  onChange={(e) => setDueDate(e.target.value)}
                />
                {currentTodo?.dueDate && (
                  <button
                    className="main-todo-due-clear"
                    onClick={() => setDueDate('')}
                    title="Clear due date"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="sub-todos-section">
            <h3 className="section-title">Sub-tasks to complete this</h3>

            <div className="sub-todos-list">
              {subTodos.length === 0 && (
                <div className="sub-empty">No sub-tasks yet. Add one below or pull from AI suggestions.</div>
              )}
              {subTodos.map((sub, idx) => (
                <div key={idx} className={`sub-todo ${sub.completed ? 'done' : ''}`}>
                  <button
                    className={`sub-todo-check ${sub.completed ? 'done' : ''}`}
                    onClick={() => toggleSubTodo(idx)}
                  >
                    {sub.completed && <Check size={12} />}
                  </button>
                  <span className="sub-todo-task">{sub.task}</span>
                  {sub.dueDate && (
                    <span className="sub-todo-due">{sub.dueDate}</span>
                  )}
                </div>
              ))}
            </div>

            <div className="sub-todo-add">
              <input
                type="text"
                value={newSubText}
                placeholder="Add a sub-task…"
                onChange={(e) => setNewSubText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addSubTodo();
                }}
              />
              <input
                type="date"
                className="sub-todo-date"
                value={newSubDate}
                required
                onChange={(e) => setNewSubDate(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addSubTodo();
                }}
                title="Due date (required)"
              />
              <button
                onClick={addSubTodo}
                disabled={!newSubText.trim() || !newSubDate.trim()}
                title={!newSubDate.trim() ? 'Pick a due date to add' : 'Add sub-task'}
              >
                <Plus size={14} />
              </button>
            </div>
          </div>
        </div>

        <aside className="todo-detail-ai">
          <div className="ai-header">
            <div className="ai-header-left">
              <Sparkles size={16} />
              <span>AI Suggestions</span>
            </div>
            <button className="ai-refresh-btn" onClick={generateSuggestions} disabled={loading} title="Regenerate">
              <RefreshCw size={14} className={loading ? 'spin' : ''} />
            </button>
          </div>

          <div className="ai-context-summary">
            <div>
              <span className="ai-context-label">Industry:</span>{' '}
              <span>{projectData?.industry || project?.industry || 'not set'}</span>
            </div>
            <div>
              <span className="ai-context-label">Books:</span>{' '}
              <span>{projectData?.bookContext || project?.bookContext || 'not set'}</span>
            </div>
            {projectData?.aiDocs && Object.keys(projectData.aiDocs).length > 0 && (
              <div className="ai-context-chip">
                <FileText size={11} />
                <span>{Object.keys(projectData.aiDocs).length} PDF{Object.keys(projectData.aiDocs).length === 1 ? '' : 's'} attached</span>
              </div>
            )}
            {projectData?.github?.fullName && (
              <div className="ai-context-chip">
                <Github size={11} />
                <span>{projectData.github.fullName}</span>
              </div>
            )}
          </div>

          {error && <div className="ai-error">{error}</div>}

          {loading && !suggestions && <div className="ai-loading">Generating suggestions…</div>}

          {suggestions && (
            <div className="ai-content">
              {Array.isArray(suggestions.subTasks) && suggestions.subTasks.length > 0 && (
                <section className="ai-section">
                  <h4>Suggested sub-tasks</h4>
                  <ul>
                    {suggestions.subTasks.map((s, i) => (
                      <li key={i}>
                        <span>{s}</span>
                        <button className="ai-import-btn" onClick={() => importSubTask(s)} title="Add as sub-task">
                          <Plus size={12} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {Array.isArray(suggestions.insights) && suggestions.insights.length > 0 && (
                <section className="ai-section">
                  <h4>Industry insights</h4>
                  <ul>
                    {suggestions.insights.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </section>
              )}

              {Array.isArray(suggestions.bookReferences) && suggestions.bookReferences.length > 0 && (
                <section className="ai-section">
                  <h4>From the books</h4>
                  <ul className="ai-books">
                    {suggestions.bookReferences.map((b, i) => (
                      <li key={i}>
                        <div className="ai-book-title">{b.title}</div>
                        <div className="ai-book-takeaway">{b.takeaway}</div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default TodoDetailPage;
